import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { Note, SaveNotePayload } from '../types';

/** Canonical key — list page, detail drawer, and any future "My Notes" widget share it. */
export const NOTES_KEY = ['notes', 'list'] as const;

/**
 * Single canonical Notes hook. Returns the notes the current user owns or that
 * are shared with them (backend scopes by owner / shared_with_users /
 * shared_with_depts). A future "My Notes" dashboard widget can consume this
 * exact hook/cache (e.g. `data.slice(0, n)`) with no changes.
 */
export function useNotes() {
  return useQuery({
    queryKey: NOTES_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ notes: Note[] }>('/api/notepad/notes');
      return data.notes ?? [];
    },
    staleTime: 60_000,
  });
}

export function useNoteMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['notes'] });

  const createNote = useMutation({
    mutationFn: async (payload: SaveNotePayload) => {
      const { data } = await api.post<{ ok: boolean; note: Note }>('/api/notepad/notes', payload);
      return data.note;
    },
    onSuccess: invalidate,
  });

  const updateNote = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: SaveNotePayload }) => {
      const { data } = await api.patch<{ ok: boolean; note: Note }>(`/api/notepad/notes/${id}`, payload);
      return data.note;
    },
    onSuccess: invalidate,
  });

  const deleteNote = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/notepad/notes/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createNote, updateNote, deleteNote };
}
